import React, { useState } from "react";
import { Heart, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { errorMessage, publicApi } from "../../lib/api";
import { paymentsApi, openCheckout, isMockOrder } from "../../lib/razorpay";

const PRESETS = [20, 50, 100];
const TIER = {
  trusted: { label: "Trusted partner", cls: "bg-emerald-100 text-emerald-700" },
  reliable: { label: "Reliable", cls: "bg-blue-100 text-blue-700" },
  building: { label: "Building trust", cls: "bg-surface-200 text-ink-soft" },
};

export function TrustBadge({ trust, className }) {
  if (!trust || !trust.eligible) return null;
  const m = TIER[trust.tier] || TIER.building;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.cls} ${className || ""}`} data-testid="trust-badge" title={`${trust.code_verified}/${trust.total} deliveries code-verified`}>
      <ShieldCheck className="h-3.5 w-3.5" /> {m.label} · {trust.pct}% verified
    </span>
  );
}

// kind: "ride" | "food"; use token for the public (receiver) tip.
export function TipDialog({ open, onOpenChange, kind = "ride", refId, token, partnerName, user, onDone }) {
  const [amt, setAmt] = useState(50);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const value = custom ? Math.round(Number(custom)) : amt;

  const pay = async () => {
    if (!value || value < 1) return toast.error("Enter a valid tip amount");
    setBusy(true);
    try {
      const order = token ? await publicApi.tipCreate(token, value) : await paymentsApi.createTip({ kind, ref_id: refId, amount: value });
      let payload = { amount: value };
      if (isMockOrder(order)) {
        payload = { ...payload, razorpay_order_id: order.order_id, razorpay_payment_id: `tip_mock_${Date.now()}`, razorpay_signature: null };
      } else {
        const res = await openCheckout({ order, amount: value, user, description: `Thank-you tip for ${partnerName || "your partner"}` });
        payload = { ...payload, razorpay_order_id: res.razorpay_order_id || order.order_id, razorpay_payment_id: res.razorpay_payment_id, razorpay_signature: res.razorpay_signature || null };
      }
      await (token ? publicApi.tipVerify(token, payload) : paymentsApi.verifyTip({ kind, ref_id: refId, ...payload }));
      toast.success(`Thank you! ₹${value} tip sent to your partner.`);
      onDone?.(value);
      onOpenChange(false);
    } catch (e) {
      if (!e?.cancelled) toast.error(errorMessage(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-sm" data-testid="tip-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Heart className="h-5 w-5 text-rose-500" /> Say thanks{partnerName ? ` to ${partnerName}` : ""}</DialogTitle>
          <DialogDescription>Add a small tip — it goes straight to your {kind === "food" ? "delivery partner" : "partner"}.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-2">
          {PRESETS.map((p) => (
            <button key={p} onClick={() => { setAmt(p); setCustom(""); }} data-testid={`tip-preset-${p}`}
              className={`rounded-xl border py-3 text-base font-bold transition-colors ${!custom && amt === p ? "border-brand bg-brand-50 text-brand" : "border-surface-300 text-ink hover:border-brand-200"}`}>
              ₹{p}
            </button>
          ))}
        </div>
        <Input type="number" min="1" inputMode="numeric" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Custom amount (₹)" className="h-11" data-testid="tip-custom" />
        <Button onClick={pay} disabled={busy} className="h-11 w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="tip-pay">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Heart className="mr-2 h-4 w-4" />} Tip ₹{value || 0}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
